// Accidents & safety (DESIGN §15). Table-driven: later eras just add rows to INCIDENTS.
//   chance/day = base × exposure × eraSafety(year) × (1 + fatigue/50) × traitMult × wobRisk × (1 − mitigation) × warnMult
import { chance, range, int, nextFloat, pick } from '../core/rng.js';
import { dayToDate } from '../core/time.js';
import { INCIDENT_FLAVOR } from '../data/news.js';
import { eqDef, fieldDef, rigStats, stat, eraDollars, unitPrice, yf, yearOf, defsByCat, isAvailable } from './defs.js';
import { strataAt, stratumAt, reservoirsAt } from './geo.js';
import { activeRigCrew, injureCrew, traitCount, roleName } from './crew.js';
import { wobFactors } from './drilling.js';
import { effects } from './research.js';
import { queueDecision, popDecision } from './security.js';
import { clamp, notify, log, spend, nextId, fmt$ } from './util.js';
import { regimeIncidentMult } from './m3/regimes.js';
import { tickBoilers, boilerRiskMult, boilerWarnText, FIRING } from './boiler.js';

const WORKING = new Set(['DRILLING', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'CLEANOUT', 'GAS_BLOW', 'GUSHER', 'COMPLETING', 'RIGGING_UP']);
const isSteam = (state, rig) => {
  const e = rig.engineUid ? state.items[rig.engineUid] : null;
  return !!e && /steam/.test(e.defId);
};
const wellOf = (state, rig) => (rig.wellId ? state.wells[rig.wellId] : null);
const working = (state, rig) => {
  const w = wellOf(state, rig);
  if (!w) return false;
  if (w.status === 'DRILLING' && w.problem) return false;
  return WORKING.has(w.status) || !!w.lift?.usesRig;
};

function h2sAt(state, well) {
  if (!well) return 0;
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId].plots[well.plot];
  const s = stratumAt(strataAt(fdef, plot), Math.max(0, well.depth - 0.5));
  let ppm = s?.h2s ?? 0;
  if (well.reservoirId) {
    const r = reservoirsAt(fdef, plot).find((x) => x.id === well.reservoirId);
    ppm = Math.max(ppm, r?.h2s ?? 0);
  }
  return ppm;
}

// ---- incident table ---------------------------------------------------------------
// exposure(state, ctx) -> number (0 = not exposed). ctx = { rig, well, crew } (rig rows) or { field, teamsters } (field rows)
export const INCIDENTS = [
  { id: 'well_fire', name: 'Well fire', fromYear: 1859, scope: 'rig', base: 0.0006, fatal: 0.25,
    exposure: (s, { rig, well }) => {
      if (!working(s, rig)) return 0;
      const gassy = Object.keys(well.gasSeen ?? {}).length > 0 || ['GUSHER', 'GAS_BLOW'].includes(well.status) || (well.reservoirId && well.status !== 'DRILLING');
      if (!gassy) return 0;
      const spacing = (rig.safety?.boilerSpacingFt ?? 30) >= 100 ? 0.4 : 1;
      return (isSteam(s, rig) ? 1 : 0.3) * spacing * (well.status === 'GUSHER' ? 4 : 1);
    },
    effect: (s, { rig, well }) => { well.fire = { day: s.day, untilDay: s.day + 4 }; const d = range(s, 20, 60); rig.condition = Math.max(0, rig.condition - d); return `${rig.name} damaged ${Math.round(d)}%.`; } },
  { id: 'fall', name: 'Fall from height', fromYear: 1859, scope: 'rig', base: 0.0004, fatal: 0.2, roles: ['tool_dresser', 'derrickman', 'laborer'],
    exposure: (s, { rig }) => {
      if (!working(s, rig)) return 0;
      const { month } = dayToDate(s.day);
      const derrick = rig.defId === 'spring_pole' ? 0.2 : 1;
      return derrick * ([12, 1, 2].includes(month) ? 1.5 : 1) * (rig.tours >= 2 ? 1.2 : 1);
    } },
  { id: 'boiler_explosion', name: 'Boiler explosion', fromYear: 1859, toYear: 1940, scope: 'rig', base: 0.0004, fatal: 0.35, victims: [1, 2],
    exposure: (s, { rig }) => {
      if (!working(s, rig) || !isSteam(s, rig)) return 0;
      const cond = s.items[rig.engineUid]?.condition ?? 100;
      // gauge & glass (sim/boiler.js): ×1 at steady firing with a full glass; crowding / low water multiply it
      return (0.2 + Math.max(0, (40 - cond) / 40) * 4) * boilerRiskMult(s, rig);
    },
    warnText: (s, { rig }) => boilerWarnText(s, rig),
    effect: (s, { rig }) => {
      const e = s.items[rig.engineUid];
      let txt = '';
      if (e && (e.condition < 40 || chance(s, 0.5))) { delete s.items[e.uid]; rig.engineUid = null; txt += 'The engine is destroyed. '; }
      const d = range(s, 30, 70);
      rig.condition = Math.max(0, rig.condition - d);
      if (rig.boiler) { rig.boiler.pressure = 0; rig.boiler.valveLifting = false; }
      return txt + `${rig.name} damaged ${Math.round(d)}%.`;
    } },
  { id: 'machinery', name: 'Caught in machinery', fromYear: 1859, scope: 'rig', base: 0.0004, fatal: 0.08, roles: ['laborer', 'tool_dresser', 'driller'],
    exposure: (s, { rig }) => (working(s, rig) ? (rig.engineUid ? 1 : 0.5) : 0) },
  { id: 'struck_by', name: 'Struck by', fromYear: 1859, scope: 'rig', base: 0.0004, fatal: 0.06, wobRisk: true,
    exposure: (s, { rig, well }) => (working(s, rig) ? (['TRIPPING', 'FISHING'].includes(well.status) ? 2 : 1) : 0) },
  { id: 'cellar_gas', name: 'Cellar gas', fromYear: 1859, scope: 'rig', base: 0.0002, fatal: 0.3,
    exposure: (s, { rig, well }) => (working(s, rig) && (Object.keys(well.gasSeen ?? {}).length || well.prod) ? 1 : 0) },
  { id: 'h2s', name: 'Sour gas (H₂S) release', fromYear: 1885, scope: 'rig', base: 0.0015, fatal: 0.4, victims: [1, 3],
    exposure: (s, { rig, well }) => (working(s, rig) ? h2sAt(s, well) / 100 : 0) },
  { id: 'teamster', name: 'Teamster accident', fromYear: 1859, scope: 'field', base: 0.0004, fatal: 0.05, roles: ['teamster'],
    exposure: (s, { field }) => {
      if (field.transportId !== 'teamster' || !(field.shippedToday > 0)) return 0;
      const { month } = dayToDate(s.day);
      return month === 3 || month === 4 ? 2 : 1;
    },
    effect: (s, { field }) => { const lost = Math.min(field.storage.bbl, 20); field.storage.bbl -= lost; return lost > 0 ? `${Math.round(lost)} bbl lost in the creek.` : ''; } },
  // Event-driven types (rolled where they happen): blowout/gas blow (drilling.js), torpedo (shootImpl), tank_fire (production.js).
];
export const INCIDENT_BY_ID = Object.fromEntries(INCIDENTS.map((i) => [i.id, i]));
const WARN_TEXT = INCIDENT_FLAVOR?.warnings ?? {};
const TEMPLATES = INCIDENT_FLAVOR?.templates ?? {};

export function newSafety() {
  return { seen: {}, warnings: {}, ignored: {}, calm: {}, daysWithoutIncident: 0, workersComp: 'ask', crewGear: {} };
}

export function rigSafety(rig) {
  return (rig.safety ??= { budget: 0, upgrades: [], daysWithoutIncident: 0, boilerSpacingFt: 30 });
}

export function eraSafety(year) {
  if (year <= 1900) return 1;
  if (year <= 1920) return 1 - 0.2 * (year - 1900) / 20;
  if (year <= 1950) return 0.8 - 0.3 * (year - 1920) / 30;
  if (year <= 1980) return 0.5 - 0.25 * (year - 1950) / 30;
  return Math.max(0.1, 0.25 - 0.1 * (year - 1980) / 20);
}

// Diminishing returns: $1/day (1860 $) ≈ −30%, $3/day ≈ −57%, cap −70%.
export function budgetMitigation(state, budget) {
  const scale = eraDollars(state, 2, 1860);
  return 0.7 * (1 - Math.exp(-(budget ?? 0) / scale));
}

export function mitigation(state, rig, type) {
  let keep = 1 - budgetMitigation(state, rig ? rigSafety(rig).budget : 0);
  const gear = [...(rig ? rigSafety(rig).upgrades : []), ...Object.keys(state.safety.crewGear ?? {})];
  for (const id of gear) keep *= 1 - (stat(eqDef(id), 'mitigates', {})[type] ?? 0);
  if (rig && activeRigCrew(state, rig).some((c) => c.role === 'safety_officer')) keep *= 0.8;
  return 1 - keep;
}

export function incidentChance(state, row, ctx) {
  const y = yf(state);
  if (y < row.fromYear || (row.toYear && y > row.toYear)) return 0;
  const exp = row.exposure(state, ctx);
  if (!(exp > 0)) return 0;
  const crew = ctx.crew ?? [];
  const fat = crew.length ? crew.reduce((a, c) => a + c.fatigue, 0) / crew.length : 0;
  const trait = (traitCount(crew, 'reckless') ? 1.4 : 1) * (traitCount(crew, 'careful') ? 0.7 : 1);
  let wob = 1;
  if (row.wobRisk && ctx.rig && ctx.well && ctx.rig.bitUid && state.items[ctx.rig.bitUid]) wob = wobFactors(state, ctx.rig, ctx.well.wob ?? 1, eqDef(state.items[ctx.rig.bitUid].defId)).riskMult;
  const key = warnKey(ctx, row.id);
  const warnMult = (state.safety.ignored[key] ?? -1) >= state.day ? 3 : (state.safety.calm[key] ?? -1) >= state.day ? 0.2 : 1;
  // M3 safety regimes (OSHA 1970, UK safety case 1993…): region- and offshore-specific incident multipliers
  const fid = ctx.well?.fieldId ?? ctx.field?.id;
  const regime = y >= 1946 && fid ? regimeIncidentMult(state, fieldDef(fid)?.regionId, { offshore: (ctx.well?.waterDepthFt ?? 0) > 0 }) : 1;
  return row.base * exp * eraSafety(y) * (1 + fat / 50) * trait * wob * (1 - mitigation(state, ctx.rig, row.id)) * warnMult * effects(state).incidentMult * regime;
}

const warnKey = (ctx, type) => `${ctx.rig?.uid ?? ctx.field?.id}:${type}`;

// ---- daily --------------------------------------------------------------------------
export function tickSafety(state) {
  const s = state.safety;
  s.daysWithoutIncident++;
  // expire warnings
  for (const w of Object.values(s.warnings)) {
    if (state.day >= w.expires) {
      if (!w.responded) { s.ignored[w.key] = state.day + 10; w.ignored = true; }
      delete s.warnings[w.id];
    }
  }
  for (const k of Object.keys(s.ignored)) if (s.ignored[k] < state.day - 30) delete s.ignored[k];
  for (const k of Object.keys(s.calm)) if (s.calm[k] < state.day) delete s.calm[k];
  tickBoilers(state); // steam gauges & water glass (deterministic) before today's rolls

  for (const rig of Object.values(state.rigs)) {
    const rs = rigSafety(rig);
    rs.daysWithoutIncident++;
    const well = wellOf(state, rig);
    if (!well) continue;
    const crew = activeRigCrew(state, rig);
    if (!crew.length) continue;
    // boiler wear
    if (isSteam(state, rig) && working(state, rig)) { const e = state.items[rig.engineUid]; e.condition = Math.max(0, e.condition - 0.04 * (FIRING[rig.boiler?.firing]?.steam ?? 1)); }
    const ctx = { rig, well, crew };
    if (rs.budget > 0) spend(state, rs.budget, 'other', false);
    for (const row of INCIDENTS) if (row.scope === 'rig') roll(state, row, ctx);
  }
  // perf: the teamster roster is the same for every field until an accident changes it (was rebuilt per field)
  let teamsters = null;
  for (const f of Object.values(state.fields)) {
    teamsters ??= Object.values(state.crew).filter((c) => c.role === 'teamster' && c.status === 'active' && !c.injuredUntil);
    if (!teamsters.length) continue;
    const ctx = { field: f, crew: teamsters, rig: null };
    for (const row of FIELD_ROWS) if (roll(state, row, ctx)) teamsters = null;
  }
}
const FIELD_ROWS = INCIDENTS.filter((r) => r.scope === 'field');

// true when an accident happened (crew may have changed)
function roll(state, row, ctx) {
  const p = incidentChance(state, row, ctx);
  if (p <= 0) return false;
  const key = warnKey(ctx, row.id);
  // warnings precede most incidents
  let hasWarn = false;
  for (const id in state.safety.warnings) if (state.safety.warnings[id].key === key) { hasWarn = true; break; }
  if (!hasWarn && chance(state, Math.min(0.3, p * 4))) issueWarning(state, row, ctx, key);
  if (chance(state, p)) { accident(state, row, ctx, key); return true; }
  return false;
}

function issueWarning(state, row, ctx, key) {
  const id = nextId(state, 'sw');
  const where = ctx.rig?.name ?? fieldDef(ctx.field?.id)?.name ?? '';
  const cost = Math.round(eraDollars(state, { boiler_explosion: 15, well_fire: 10, h2s: 10, fall: 5, machinery: 5, struck_by: 3, cellar_gas: 5, teamster: 5 }[row.id] ?? 5, 1860) * 100) / 100;
  const w = { id, key, type: row.id, rigUid: ctx.rig?.uid ?? null, wellId: ctx.well?.id ?? null, fieldId: ctx.field?.id ?? ctx.well?.fieldId ?? null, day: state.day, expires: state.day + 5, cost, responded: false, text: `${where}: ${row.warnText?.(state, ctx) ?? WARN_TEXT[row.id] ?? `Signs of trouble (${row.name})`}.` };
  state.safety.warnings[id] = w;
  notify(state, 'warning', { warningId: id, incidentType: row.id, wellId: w.wellId, fieldId: w.fieldId, text: `${w.text} Respond for ${fmt$(cost)}?`, level: 'warn' });
}

export function respondWarningImpl(state, warningId) {
  const w = state.safety.warnings[warningId];
  if (!w) return { ok: false, msg: 'That warning has passed.' };
  if (w.responded) return { ok: false, msg: 'Already handled.' };
  if (!spend(state, w.cost, 'other')) return { ok: false, msg: `Costs ${fmt$(w.cost)}.` };
  w.responded = true;
  state.safety.calm[w.key] = state.day + 20;
  delete state.safety.warnings[warningId];
  return { ok: true, msg: `Dealt with: ${w.text}` };
}

function fill(tpl, vars) { return tpl.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? ''); }

export function accident(state, row, ctx, key) {
  const where = ctx.rig?.name ?? '';
  const fieldName = fieldDef(ctx.well?.fieldId ?? ctx.field?.id)?.name ?? '';
  const pool = (row.roles ? ctx.crew.filter((c) => row.roles.includes(c.role)) : ctx.crew);
  const victimsPool = pool.length ? pool : ctx.crew;
  if (!victimsPool.length) return;
  // first-time grace: a near miss teaches the hazard
  if (!state.safety.seen[row.id]) {
    state.safety.seen[row.id] = state.day;
    const v = victimsPool[0];
    notify(state, 'warning', { nearMiss: true, incidentType: row.id, wellId: ctx.well?.id ?? null, text: `Near miss — ${row.name}: ${fill(pick(state, TEMPLATES[row.id] ?? ['{name} nearly got hurt at {rig}.']), { rig: where, field: fieldName, name: v.name })} Nobody hurt this time. ${WARN_TEXT[row.id] ? `Watch for: “${WARN_TEXT[row.id]}”.` : ''}`, level: 'warn' });
    return;
  }
  const preventable = (state.safety.ignored[key] ?? -1) >= state.day;
  const [lo, hi] = row.victims ?? [1, 1];
  const n = Math.min(victimsPool.length, int(state, lo, hi));
  const hit = [];
  const avail = [...victimsPool];
  for (let i = 0; i < n; i++) hit.push(avail.splice(Math.floor(nextFloat(state) * avail.length), 1)[0]);
  const results = hit.map((c) => ({ c, r: injureCrew(state, c, `${row.name.toLowerCase()}`, { cause: row.name, fatalChance: row.fatal, days: [10, 90], wellId: ctx.well?.id ?? null, accident: row.id, silent: true, preventable }) }));
  const effTxt = row.effect ? row.effect(state, ctx) : '';
  if (ctx.rig) rigSafety(ctx.rig).daysWithoutIncident = 0;
  state.safety.daysWithoutIncident = 0;
  const head = fill(pick(state, TEMPLATES[row.id] ?? [`${row.name} at {rig}.`]), { rig: where, field: fieldName, name: hit[0].name });
  const fates = results.map(({ c, r }) => `${c.name} (${roleName(c.role)}) ${r === 'dead' ? 'was killed' : 'was injured'}`).join('; ');
  notify(state, 'accident', { incidentType: row.id, wellId: ctx.well?.id ?? null, fieldId: ctx.well?.fieldId ?? ctx.field?.id ?? null, rigUid: ctx.rig?.uid ?? null, pause: true, preventable, fatal: results.some((x) => x.r === 'dead'), text: `${head} ${fates}. ${effTxt}${preventable ? ' The warning signs were ignored.' : ''}`, level: 'warn' });
}

// ---- workers' compensation ----------------------------------------------------------
export function compAmount(state, c) { return Math.round((c.wage || 1.5) * 300); }

export function handleDeath(state, c, cause) {
  const amt = compAmount(state, c);
  const mandatory = yearOf(state) >= 1911;
  if (mandatory || state.safety?.workersComp === 'always') {
    spend(state, amt, 'other', false);
    log(state, `Paid ${fmt$(amt)} compensation to the family of ${c.name}.`);
    return;
  }
  if (state.safety?.workersComp === 'never') return;
  queueDecision(state, {
    kind: 'workersComp', id: `wc_${c.id}`, crewId: c.id, day: state.day, amount: amt,
    title: `${c.name} (${roleName(c.role)})`,
    text: `${c.name} died (${cause}). There is no law yet that says you must pay the family anything. The crew is watching.`,
    options: [{ choice: 'pay', label: `Pay the widow ${fmt$(amt)}`, cost: amt }, { choice: 'decline', label: 'Pay nothing' }],
  });
}

export function resolveCompImpl(state, decisionId, choice) {
  const d = [state.pendingDecision, ...(state.decisionQueue ?? [])].find((x) => x && x.id === decisionId && x.kind === 'workersComp');
  if (!d) return { ok: false, msg: 'Nothing to decide.' };
  if (choice === 'pay') {
    if (!spend(state, d.amount, 'other')) return { ok: false, msg: `Need ${fmt$(d.amount)}.` };
    for (const o of Object.values(state.crew)) if (o.status === 'active') o.morale = Math.min(100, o.morale + 5);
    state.company.reputation = Math.min(100, state.company.reputation + 3);
    popDecision(state, decisionId);
    return { ok: true, msg: 'The family is provided for. The crew notices.' };
  }
  for (const o of Object.values(state.crew)) if (o.status === 'active') o.morale = Math.max(0, o.morale - 10);
  state.company.reputation = Math.max(0, state.company.reputation - 2);
  popDecision(state, decisionId);
  return { ok: true, msg: 'Nothing paid. The crew mutters.' };
}

// ---- actions ------------------------------------------------------------------------
export function setSafetyBudgetImpl(state, rigUid, perDay) {
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  rigSafety(rig).budget = Math.max(0, Number(perDay) || 0);
  return { ok: true, msg: `${rig.name} safety budget ${fmt$(rigSafety(rig).budget)}/day (−${Math.round(budgetMitigation(state, rigSafety(rig).budget) * 100)}% incidents).` };
}

export function setBoilerSpacingImpl(state, rigUid, ft) {
  const rig = state.rigs[rigUid];
  if (!rig) return { ok: false, msg: 'No such rig.' };
  const rs = rigSafety(rig);
  const extra = Math.max(0, ft - rs.boilerSpacingFt);
  const cost = eraDollars(state, 0.5 * extra, 1860); // longer belts & steam line
  if (cost > 0 && !spend(state, cost, 'capex')) return { ok: false, msg: `Needs ${fmt$(cost)}.` };
  rs.boilerSpacingFt = Math.max(10, ft);
  return { ok: true, msg: `Boiler set ${rs.boilerSpacingFt} ft from the derrick${rs.boilerSpacingFt >= 100 ? ' — gas fires much less likely' : ''}.` };
}

// buyEquipment for cat 'safety': perRig → every rig lacking it; perTank → every tank lacking it; perCrew → company-wide gear.
export function buySafetyImpl(state, def, rigUid = null) {
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} isn't available yet.` };
  const price = unitPrice(state, def);
  if (stat(def, 'perCrew', false)) {
    const n = Object.values(state.crew).filter((c) => c.status === 'active').length || 1;
    if (state.safety.crewGear[def.id]) return { ok: false, msg: 'Everyone already has one.' };
    if (!spend(state, price * n, 'capex')) return { ok: false, msg: `Need ${fmt$(price * n)} for ${n} crew.` };
    state.safety.crewGear[def.id] = true;
    return { ok: true, msg: `${def.name} for all ${n} hands.` };
  }
  if (stat(def, 'perTank', false)) {
    const tanks = Object.values(state.items).filter((i) => i.loc.type === 'field' && eqDef(i.defId)?.cat === 'storage' && !(i.upgrades ?? []).includes(def.id));
    if (!tanks.length) return { ok: false, msg: 'No unprotected tanks.' };
    if (!spend(state, price * tanks.length, 'capex')) return { ok: false, msg: `Need ${fmt$(price * tanks.length)}.` };
    for (const t of tanks) (t.upgrades ??= []).push(def.id);
    return { ok: true, msg: `${def.name} fitted to ${tanks.length} tank(s).` };
  }
  const rigs = (rigUid ? [state.rigs[rigUid]] : Object.values(state.rigs)).filter((r) => r && !rigSafety(r).upgrades.includes(def.id));
  if (!rigs.length) return { ok: false, msg: 'Every rig already has one.' };
  if (!spend(state, price * rigs.length, 'capex')) return { ok: false, msg: `Need ${fmt$(price * rigs.length)}.` };
  for (const r of rigs) rigSafety(r).upgrades.push(def.id);
  return { ok: true, msg: `${def.name} fitted to ${rigs.map((r) => r.name).join(', ')}.` };
}

// Tank fire mitigation for production.js (lightning rods).
export function tankFireMult(state, tank) {
  let keep = 1;
  for (const id of tank.upgrades ?? []) keep *= 1 - (stat(eqDef(id), 'mitigates', {}).tank_fire ?? 0);
  return keep;
}
